import { AIRO_LOCKED_TEXT, AIRO_QUERY_VERSE, isAiroLicensed } from "../../services/license.service";
import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ChangeDetectorRef,
} from "@angular/core";
import { AiroService } from "../../services/airo.service";
import { Formatter } from "src/app/shared/pipes/app-format.pipe";
import { FinancialService } from "../../services/financial.service";
import { Router } from "@angular/router";
import { MatExpansionPanel } from "@angular/material/expansion";

@Component({
  selector: "app-signals",
  templateUrl: "./signals.component.html",
  styleUrls: ["./signals.component.scss"],
})
export class SignalsComponent implements OnInit {
  @Input() zone: string = "opportunity_workbench";
  @ViewChild("recentPanel") recentPanel!: MatExpansionPanel;
  query: string = "";
  summary: string = "";
  links: { name: string; url: string }[] = [];
  isLoading: boolean = false;
  recentQueries: any = [];
  generatedAt: any;
  enterpriseDetails: any;
  airoResp: any;
  // Ask Airo needs the tenant's Airo licence to include Query Verse.
  readonly airoLocked = !isAiroLicensed(AIRO_QUERY_VERSE);
  readonly airoLockedText = AIRO_LOCKED_TEXT;

  constructor(
    private readonly airoService: AiroService,
    private readonly financialService: FinancialService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.airoLocked) return;
    this.getRecentConversations();
    this.financialService.getEnterpriseDetails().subscribe((res: any) => {
      this.enterpriseDetails = res[0];
    });
  }

  getRecentConversations() {
    this.airoService
      .getPreviousConversations("signals")
      .subscribe((res: any) => {
        this.recentQueries = res.data;
      });
  }

  processResponse(res: any) {
    const acceptableKeys = [
      "title",
      "context",
      "problem_statement",
      "description",
    ];
    this.summary = acceptableKeys
      .filter((key) => res[key])
      .map((key) => res[key])
      .join("\n\n");
    this.generatedAt = res.generated_at;

    this.links = res.data.map((item: any) => ({
      name: item.name,
      url: item.url,
    }));
  }

  onAskAiro() {
    if (this.airoLocked) return;
    if (!this.query.trim()) return;

    this.isLoading = true;
    this.summary = "";
    this.links = [];

    const payload = {
      query_string: this.query,
      zone: this.zone,
    };

    this.airoService.getWebResult(payload).subscribe(
      (res: any) => {
        this.airoResp = res.data;
        this.processResponse(res.data);
        this.isLoading = false;
        this.getRecentConversations();
      },
      (err: any) => {
        console.error("Error fetching web result:", err);
        this.isLoading = false;
      }
    );
  }

  loadQuery(q: any) {
    this.query = q.query;
    this.airoResp = q.response;
    this.summary = "";
    this.links = [];
    this.generatedAt = null;
    this.cdr.detectChanges(); // flush the clear
    this.processResponse(q.response);
    this.recentPanel.close();
  }

  formatTime(createdOn: string) {
    const date = new Date(createdOn);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  openUrl(link: any) {
    window.open(link, "_blank");
  }

  exportToPDF(): void {
    this.generatePDFBlob().then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "signal-analysis-report.pdf";
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  generatePDFBlob(): Promise<Blob> {
    return import("jspdf").then(async ({ jsPDF }) => {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const usableWidth = pageWidth - margin * 2;
      const contentBottom = pageHeight - 80; // reserve space for footer
      let y = 40;

      // --- Logo (top left) ---
      const logoPath = "assets/images/pdfs/logo.png";
      try {
        const logo = await this.loadImage(logoPath);
        doc.addImage(logo, "PNG", margin, y, 80, 40);
      } catch {
        // optional
      }
      y += 50;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(33, 33, 33);
      doc.text("Signal Analysis Report", pageWidth / 2, y + 28, {
        align: "center",
      });
      y += 60;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(120, 120, 120);

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 20;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(50, 50, 50);

      const summary = this.summary || "";

      const parseMarkdownSegments = (text: string) => {
        const segments: Array<{ text: string; url?: string }> = [];
        const mdRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = mdRegex.exec(text)) !== null) {
          if (m.index > lastIndex) {
            segments.push({ text: text.substring(lastIndex, m.index) });
          }
          segments.push({ text: m[1], url: m[2] });
          lastIndex = mdRegex.lastIndex;
        }
        if (lastIndex < text.length)
          segments.push({ text: text.substring(lastIndex) });
        return segments;
      };

      const lineHeight = 16;

      const renderParagraph = (para: string) => {
        const segments = parseMarkdownSegments(para);
        const words: Array<{ text: string; url?: string }> = [];
        segments.forEach((seg) => {
          const parts = seg.text.split(/(\s+)/);
          for (const part of parts) {
            if (part === "") continue;
            if (/^\s+$/.test(part)) {
              words.push({ text: part, url: undefined });
            } else {
              words.push({ text: part, url: seg.url });
            }
          }
        });

        let lineWords: Array<{ text: string; url?: string }> = [];
        let lineWidth = 0;

        const flushLine = () => {
          if (lineWords.length === 0) return;
          if (y > contentBottom) {
            doc.addPage();
            y = 60;
          }
          let x = margin;
          for (const w of lineWords) {
            const textToRender = w.text;
            if (w.url) {
              doc.setTextColor(0, 102, 204);
              doc.textWithLink(textToRender, x, y, { url: w.url });
              doc.setTextColor(50, 50, 50);
            } else {
              doc.setTextColor(50, 50, 50);
              doc.text(textToRender, x, y);
            }
            x += doc.getTextWidth(textToRender);
          }
          y += lineHeight;
          lineWords = [];
          lineWidth = 0;
        };

        for (const w of words) {
          const wWidth = doc.getTextWidth(w.text);
          if (lineWidth + wWidth <= usableWidth || lineWidth === 0) {
            lineWords.push(w);
            lineWidth += wWidth;
          } else {
            flushLine();
            if (wWidth > usableWidth) {
              const parts = doc.splitTextToSize(w.text, usableWidth);
              for (let i = 0; i < parts.length; i++) {
                const part = parts[i];
                if (i > 0) {
                  if (y > contentBottom) {
                    doc.addPage();
                    y = 60;
                  }
                }
                if (w.url) {
                  doc.setTextColor(0, 102, 204);
                  doc.textWithLink(part, margin, y, { url: w.url });
                  doc.setTextColor(50, 50, 50);
                } else {
                  doc.setTextColor(50, 50, 50);
                  doc.text(part, margin, y);
                }
                y += lineHeight;
              }
            } else {
              lineWords.push(w);
              lineWidth += wWidth;
            }
          }
        }
        flushLine();
      };

      const paragraphs = summary.split(/\r?\n/);
      for (const para of paragraphs) {
        if (para.trim() === "") {
          y += lineHeight;
          continue;
        }
        renderParagraph(para);
      }

      if (this.links?.length) {
        y += 20;
        if (y > contentBottom - 40) {
          doc.addPage();
          y = 60;
        }

        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y, pageWidth - margin, y);
        y += 20;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(33, 33, 33);
        doc.text("Sources", margin, y);
        y += 20;

        this.links.forEach((link, i) => {
          if (y > contentBottom) {
            doc.addPage();
            y = 60;
          }
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(80, 80, 80);
          doc.text(`${i + 1}.`, margin, y);

          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 102, 204);
          const linkLabel = link.name || link.url;
          const truncated = doc.splitTextToSize(linkLabel, usableWidth - 20)[0];
          doc.textWithLink(truncated, margin + 18, y, { url: link.url });

          y += 18;
        });
      }

      const totalPages = (doc as any).internal.getNumberOfPages();
      const genDate = this.formatGenDate(new Date());

      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, pageHeight - 65, pageWidth - margin, pageHeight - 65);

        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(150, 150, 150);
        doc.text(
          `This PDF is digitally generated on ${genDate}.`,
          pageWidth / 2,
          pageHeight - 50,
          { align: "center" }
        );

        doc.setFont("helvetica", "normal");
        doc.text(
          `${this.enterpriseDetails?.address || ""} | Tel: ${this.enterpriseDetails?.telephone || ""} | Fax: ${this.enterpriseDetails?.fax || ""}`,
          pageWidth / 2,
          pageHeight - 35,
          { align: "center" }
        );

        doc.text(
          `${this.enterpriseDetails?.website || ""} | ${this.enterpriseDetails?.email || ""} | CIN: ${this.enterpriseDetails?.cin || ""}`,
          pageWidth / 2,
          pageHeight - 20,
          { align: "center" }
        );

        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Page ${p} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 20,
          { align: "right" }
        );
      }

      const blob = doc.output("blob");
      return blob;
    });
  }

  loadImage(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(url);
      img.onerror = reject;
      img.src = url;
    });
  }

  formateDatePDF(date: Date) {
    return Formatter.formatDate(date);
  }

  formatGenDate(date: Date | string | number): string {
    if (!date) return "";

    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const formattedDate = this.formateDatePDF(d);

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "UTC",
      timeZoneName: "short",
    };
    const timePart = d.toLocaleTimeString("en-US", timeOptions);
    return `${formattedDate} ${timePart}`;
  }

  clearResults() {
    this.query = "";
    this.links = [];
    this.summary = "";
    this.generatedAt = null;
  }

  async create() {
    let navigationArray: any[] = [];
    if (this.zone === "opportunity_workbench") {
      navigationArray = ["challenge-workbench/create-statement/", btoa("new")];
    } else if (this.zone === "okr_convergence") {
      navigationArray = ["okrconvergence/okr-register"];
    }
    try {
      const blob = await this.generatePDFBlob();
      const file = new File(
        [blob],
        `${this.query}signal-analysis-report-generated-at-${this.generatedAt}.pdf`,
        {
          type: "application/pdf",
        }
      );

      this.router.navigate(navigationArray, {
        state: {
          prefillData: {
            airoResp: this.airoResp,
            file: file,
          },
        },
      });
    } catch (err) {
      console.error("Error generating PDF for prefill:", err);
      // fallback: navigate without file
      this.router.navigate(navigationArray, {
        state: {
          prefillData: {
            airoResp: this.airoResp,
          },
        },
      });
    }
  }
}
